import {
	connectToRouter,
	Navigation,
	NavigationCallback,
	PredefinedPropOptionKind
} from "client/Navigation";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	createContext,
	Dispatch,
	FunctionComponent,
	PropsWithChildren,
	ReactElement,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { ErrorPageContainer } from "../ErrorPage/ErrorPageContainer";
import { ErrorPage } from "../ErrorPage/ErrorPage";
import { GlobalNavigationSidebar } from "../Layout/GlobalNavigationSidebar/GlobalNavigationSidebar";
import { localize } from "client_legacy/ndaw/culture";
import { AppType } from "client/Api/Apps/common";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	CommunityHubSidebarNodeModel,
	CommunityRootHubSidebarNodeModel,
	getPageViewModel,
	HubDataSource,
	HubItemHubSidebarNodeModel,
	RootHubSidebarNodeModel
} from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { useHubSidebarManagementDialog } from "./HubSidebarManagementDialog/HubSidebarManagementDialog";
import { useAnalyticsStore } from "../Analytics/analyticsStore";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { GlobalNavigationCriteria, useGlobalNavigationStore } from "../Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import { useHandleNavigationInGlobalSubNavigation } from "../Layout/GlobalNavigationSidebar/useGlobalNavigationVisibilityState";
import { HubItemCategoryNodeKind } from "client/Api/Hub/common";
import { never } from "client_legacy/ndaw/errors";
import { BusyIndicator } from "client/Components";
import styles from "./HubLayout.module.less";
import { outOfContextCallError } from "client/Misc/reactUtils";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import {
	convertNodeModelsToSidebarNodes,
	HubRootNode,
	HubSidebar,
	HubSidebarValue,
} from "./Sidebar/HubSidebar";
import { getHubNavigationForPage } from "./HubOverviewPage";
import { getHubNavigationForCommunity } from "./Communities/CommunitiesPage";
import { getHubNavigationForEvent } from "./Events/EventsPage";
import { getHubNavigationForKnowledgeItem } from "./KnowledgeItems/KnowledgeItemsPage";
import { ActiveFilter } from "./common";
import { usePageTitle } from "client/Hooks/usePageTitle";
import {
	HubEvent,
	subscribe as subscribeHubEvent
} from "./events";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import {
	CommunityEvent,
	subscribe as subscribeCommunityEvent
} from "./Communities/events";
import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";
import { CommunityKind } from "client/Api/Communities/community";
import { getHubNavigationForAnnouncement } from "./Announcements/AnnouncementsPage";

type HubLayoutProps = {
	onNavigate: NavigationCallback;
};

const HubLayout = (props: PropsWithChildren<HubLayoutProps>): ReactElement => {
	const {
		onNavigate,
		children
	} = props;

	const { isHubAppAvailable } = userConfiguration();

	if (!isHubAppAvailable) {
		return (
			<ErrorPageContainer>
				<ErrorPage httpCode={403} />
			</ErrorPageContainer>
		);
	}

	return (
		<AccessibleHubLayout
			onNavigate={onNavigate}
		>
			{children}
		</AccessibleHubLayout>
	);
};
HubLayout.displayName = "HubLayout";

type AccessibleHubLayoutProps = {
	onNavigate: NavigationCallback;
};

const AccessibleHubLayout = (props: PropsWithChildren<AccessibleHubLayoutProps>): ReactElement => {
	const {
		onNavigate,
		children
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [hierarchy, setHierarchy] = useState<readonly RootHubSidebarNodeModel[]>([]);
	const [sidebarValue, setSidebarValue] = useState<HubSidebarValue | undefined>(undefined);
	const [hubItemKindsCanCreateNewCategoriesFor, setHubItemKindsCanCreateNewCategoriesFor] = useState<readonly HubItemKind[]>([]);
	const [canAccessHubManagementForCommunities, setCanAccessHubManagementForCommunities] = useState<boolean>(false);
	const [canManageCommunityMembershipRequests, setCanManageCommunityMembershipRequests] = useState<boolean>(false);
	const [communityCreationPermissions, setCommunityCreationPermissions] = useState<CommunityCreationPermissions>({
		canCreateCommunity: false,
		canCreateCommunityPost: false
	});

	const [canManageSidebar, setCanManageSidebar] = useState<boolean>(false);
	const [blockHandlers, setBlockHandlers] = useState<readonly RegisteredBlockHandler[]>([]);
	const [activeFilter, setActiveFilter] = useState<ActiveFilter | null>(null);

	const sidebarNodes = useMemo(
		(): readonly HubRootNode[] => convertNodeModelsToSidebarNodes(hierarchy),
		[hierarchy]
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const [hubSidebarManagementDialog, openHubSidebarManagementDialog] = useHubSidebarManagementDialog();
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);
	const setActiveItem: (criteria: GlobalNavigationCriteria) => () => void = useGlobalNavigationStore(
		x => x.setActiveCriteria
	);

	const handleNavigationInGlobalSubNavigation: () => void = useHandleNavigationInGlobalSubNavigation();

	const fetchPageViewModel = useCallback(
		async (): Promise<void> => {
			setIsBusy(true);

			try {
				const {
					categoryHierarchy,
					hubItemKindsCanCreateNewCategoriesFor,
					canAccessHubManagementForCommunities,
					canManageCommunityMembershipRequests,
					canManageSidebar,
					canCreateCommunity,
					canCreateCommunityPost
				} = await getPageViewModel(globalCancellation);

				setHierarchy(categoryHierarchy);
				setHubItemKindsCanCreateNewCategoriesFor(hubItemKindsCanCreateNewCategoriesFor);
				setCanAccessHubManagementForCommunities(canAccessHubManagementForCommunities);
				setCanManageCommunityMembershipRequests(canManageCommunityMembershipRequests);
				setCanManageSidebar(canManageSidebar);
				setCommunityCreationPermissions({
					canCreateCommunity,
					canCreateCommunityPost
				});

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[alertManager, globalCancellation]
	);

	usePageTitle(localize("_TheHub"));

	usePubSubEvents(
		setHierarchy,
		fetchPageViewModel
	);

	useEffect(
		(): (() => void) => {
			addAnalyticsAppEvent({
				appType: AppType.Hub
			});

			return setActiveItem({
				type: "app",
				appType: AppType.Hub
			});
		},
		[setActiveItem, addAnalyticsAppEvent]
	);

	useEffect(
		(): void => {
			fetchPageViewModel();
		},
		[fetchPageViewModel]
	);

	const changeSelectedNodeHandler = useCallback(
		async (value: HubSidebarValue): Promise<void> => {
			handleNavigationInGlobalSubNavigation();

			for (const blockHandler of blockHandlers) {
				const shouldBlock: boolean = await blockHandler.handler();

				if (shouldBlock) {
					return;
				}
			}

			let navigation: Navigation;

			switch (value.source) {
				case undefined:
					navigation = getHubNavigationForPage();
					break;
				case HubDataSource.KnowledgeItem:
					navigation = getHubNavigationForKnowledgeItem({
						source: HubDataSource.KnowledgeItem,
						viewIdentifier: value.viewIdentifier
					});
					break;
				case HubDataSource.Announcement:
					navigation = getHubNavigationForAnnouncement({
						source: HubDataSource.Announcement,
						categoryId: typeof value.categoryId === "number"
							? value.categoryId
							: undefined,
						itemId: undefined
					});
					break;
				case HubDataSource.Event:
					navigation = getHubNavigationForEvent({
						source: HubDataSource.Event,
						categoryId: typeof value.categoryId === "number"
							? value.categoryId
							: undefined,
						itemId: undefined
					});
					break;
				case HubDataSource.Community:
					navigation = getHubNavigationForCommunity({
						source: HubDataSource.Community,
						communityId: value.communityId
					});
					break;
				default:
					never(value);
			}

			onNavigate(navigation);
		},
		[
			blockHandlers,
			handleNavigationInGlobalSubNavigation,
			onNavigate
		]
	);

	const actions = useMemo(
		(): readonly Action<undefined>[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "manage-hub-sidebar",
					icon: "gear",
					title: localize("_ManageHubSidebar"),
					canExecute: (): boolean => canManageSidebar,
					execute: async (): Promise<void> => {
						await openHubSidebarManagementDialog({});
					}
				}
			];
		},
		[
			canManageSidebar,
			openHubSidebarManagementDialog
		]
	);

	const layoutContext = useMemo(
		(): HubLayoutContextData => ({
			hierarchy,
			hubItemKindsCanCreateNewCategoriesFor,
			canAccessHubManagementForCommunities,
			canManageCommunityMembershipRequests,
			activeFilter,
			communityCreationPermissions,
			changeActiveFilter: setActiveFilter,
			changeActiveSidebarItem: setSidebarValue,
			addOnBeforeNavigateToSidebarItemHandler: (handler: () => Promise<boolean> | boolean) => {
				const registration: RegisteredBlockHandler = {
					id: newGuid(),
					handler
				};

				setBlockHandlers((x) => [...x, registration]);

				return (): void => {
					setBlockHandlers((x) => x.filter((r) => r.id !== registration.id));
				};
			}
		}),
		[
			activeFilter,
			canAccessHubManagementForCommunities,
			canManageCommunityMembershipRequests,
			hierarchy,
			hubItemKindsCanCreateNewCategoriesFor,
			communityCreationPermissions
		]
	);

	return (
		<>
			<GlobalNavigationSidebar.ActivePanelOverride
				title={localize("_TheHub")}
				kind={AppType.Hub}
				actions={actions}
				actionContext={undefined}
			>
				<HubSidebar
					rootNodes={sidebarNodes}
					value={sidebarValue}
					onChange={changeSelectedNodeHandler}
					activeFilter={activeFilter}
					areActionsAvailable
				/>
			</GlobalNavigationSidebar.ActivePanelOverride>

			<div className={styles.root}>
				{isBusy && <BusyIndicator />}

				<HubLayoutContext.Provider value={layoutContext}>
					{children}
				</HubLayoutContext.Provider>
			</div>

			{hubSidebarManagementDialog}
		</>
	);
};
AccessibleHubLayout.displayName = "AccessibleHubLayout";

type RegisteredBlockHandler = {
	id: Guid;
	handler: () => Promise<boolean> | boolean;
};

export type HubLayoutContextData = {
	hierarchy: readonly RootHubSidebarNodeModel[];
	hubItemKindsCanCreateNewCategoriesFor: readonly HubItemKind[];
	canAccessHubManagementForCommunities: boolean;
	canManageCommunityMembershipRequests: boolean;
	communityCreationPermissions: CommunityCreationPermissions;
	activeFilter: ActiveFilter | null;
	changeActiveFilter: (value: ActiveFilter | null) => void;
	changeActiveSidebarItem: (value: HubSidebarValue) => void;
	addOnBeforeNavigateToSidebarItemHandler: (handler: () => Promise<boolean> | boolean) => (() => void);
};

export const HubLayoutContext = createContext<HubLayoutContextData>({
	hierarchy: [],
	hubItemKindsCanCreateNewCategoriesFor: [],
	canAccessHubManagementForCommunities: false,
	canManageCommunityMembershipRequests: false,
	activeFilter: null,
	communityCreationPermissions: {
		canCreateCommunity: false,
		canCreateCommunityPost: false
	},
	changeActiveFilter: outOfContextCallError("HubLayoutContext", "changeActiveFilter"),
	changeActiveSidebarItem: outOfContextCallError("HubLayoutContext", "onChangeActiveSidebarItem"),
	addOnBeforeNavigateToSidebarItemHandler: outOfContextCallError("HubLayoutContext", "onChangeActiveSidebarItem")
});

function usePubSubEvents(
	setHierarchy: Dispatch<SetStateAction<readonly RootHubSidebarNodeModel[]>>,
	reloadHierarchy: () => void
): void {
	useEffect(
		(): (() => void) => {
			const subscriptionTokens: SubscriptionTokenCollection = registerSubscriptions(
				setHierarchy,
				reloadHierarchy
			);

			return (): void => subscriptionTokens.destroy();
		},
		[
			setHierarchy,
			reloadHierarchy
		]
	);
}

function registerSubscriptions(
	setHierarchy: Dispatch<SetStateAction<readonly RootHubSidebarNodeModel[]>>,
	reloadHierarchy: () => void
): SubscriptionTokenCollection {
	const result = new SubscriptionTokenCollection();

	const followingChangedToken: PubSubSubscriptionToken = subscribeHubEvent(
		HubEvent.CategoryFollowingChanged,
		(_, data) => {
			const { categoryId, isFollowing } = data;

			setHierarchy(
				(rootItems): readonly RootHubSidebarNodeModel[] => {
					return rootItems
						.map(
							(rootItem): RootHubSidebarNodeModel => {
								switch (rootItem.source) {
									case HubDataSource.Announcement:
									case HubDataSource.KnowledgeItem:
									case HubDataSource.Event:
										return {
											...rootItem,
											children: rootItem
												.children
												.map(
													x => updateCategoryFollowStatus(
														categoryId,
														isFollowing,
														x
													)
												)
										};
									case HubDataSource.Community:
										return rootItem;
									default:
										return never(rootItem);
								}
							}
						);
				}
			);
		}
	);

	result.add(followingChangedToken);

	const isNewStatusClearedToken: PubSubSubscriptionToken = subscribeHubEvent(
		HubEvent.IsNewStatusCleared,
		(_, data) => {
			let target: HubSideBarNewItemReductionTarget;

			switch (data.kind) {
				case HubDataSource.Community:
					target = {
						itemKind: HubDataSource.Community,
						communityId: data.communityId
					};
					break;
				case HubDataSource.Announcement:
					target = {
						itemKind: data.kind,
						categoryIds: data.categoryIds
					};
					break;
				case HubDataSource.KnowledgeItem:
				case HubDataSource.Event:
					target = {
						itemKind: data.kind,
						categoryIds: [data.categoryId]
					};
					break;
				default:
					never(data);
			}

			setHierarchy(
				rootItems => reduceHubSidebarNewItemCountByOne(
					rootItems,
					target
				)
			);
		}
	);

	result.add(isNewStatusClearedToken);

	const communityCreatedToken: PubSubSubscriptionToken = subscribeCommunityEvent(
		CommunityEvent.CommunityCreated,
		(_, data) => {
			setHierarchy(
				(x): readonly RootHubSidebarNodeModel[] => mapCommunitiesNodes(
					x,
					(rootItem) => {
						let result: CommunityRootHubSidebarNodeModel = rootItem;

						if (data.isMember) {
							result = {
								...result,
								children: insertCustomCommunity(
									result.children,
									data
								)
							};
						} else {
							result = {
								...result,
								newCommunityIds: result.newCommunityIds.includes(data.id)
									? result.newCommunityIds
									: [...result.newCommunityIds, data.id]
							};
						}

						return result;
					}
				)
			);
		}
	);

	const joinedCommunityToken: PubSubSubscriptionToken = subscribeCommunityEvent(
		CommunityEvent.JoinedCommunity,
		(_, community) => {
			setHierarchy(
				(x): readonly RootHubSidebarNodeModel[] => mapCommunitiesNodes(
					x,
					(rootItem) => {
						return {
							...rootItem,
							children: insertCustomCommunity(
								rootItem.children,
								community
							)
						};
					}
				)
			);
		}
	);

	const leftCommunityToken: PubSubSubscriptionToken = subscribeCommunityEvent(
		CommunityEvent.LeftCommunity,
		(_, data) => {
			setHierarchy(
				(x): readonly RootHubSidebarNodeModel[] => mapCommunitiesNodes(
					x,
					(rootItem) => {
						return {
							...rootItem,
							children: removeCommunity(
								rootItem.children,
								data.communityId
							)
						};
					}
				)
			);
		}
	);

	const communityDeletedToken: PubSubSubscriptionToken = subscribeCommunityEvent(
		CommunityEvent.CommunityDeleted,
		(_, data) => {
			setHierarchy(
				(x): readonly RootHubSidebarNodeModel[] => mapCommunitiesNodes(
					x,
					(rootItem) => {
						return {
							...rootItem,
							children: removeCommunity(
								rootItem.children,
								data.communityId
							)
						};
					}
				)
			);
		}
	);

	const hierarchyChangedToken: PubSubSubscriptionToken = subscribeHubEvent(
		HubEvent.HierarchyChanged,
		reloadHierarchy
	);

	result.add(communityCreatedToken);
	result.add(joinedCommunityToken);
	result.add(leftCommunityToken);
	result.add(communityDeletedToken);
	result.add(hierarchyChangedToken);

	return result;
}

function mapCommunitiesNodes(
	rootItems: readonly RootHubSidebarNodeModel[],
	selector: (rootItem: CommunityRootHubSidebarNodeModel) => CommunityRootHubSidebarNodeModel
): readonly RootHubSidebarNodeModel[] {
	return rootItems
		.map(
			(rootItem): RootHubSidebarNodeModel => {
				switch (rootItem.source) {
					case HubDataSource.Announcement:
					case HubDataSource.KnowledgeItem:
					case HubDataSource.Event:
						return rootItem;
					case HubDataSource.Community:
						return selector(rootItem);
					default:
						return never(rootItem);
				}
			}
		);
}

function insertCustomCommunity(
	communities: readonly CommunityHubSidebarNodeModel[],
	community: { id: number; name: string; }
): readonly CommunityHubSidebarNodeModel[] {
	const {
		id,
		name
	} = community;

	const indexToInsert: number = communities
		.findIndex(
			(x) => {
				if (x.kind === CommunityKind.AllCompany) {
					return false;
				}

				return nativeSortFunction(
					name,
					x.name,
					DataSourceSortDirection.Ascending
				) <= 0;
			}
		);

	const result: CommunityHubSidebarNodeModel[] = [...communities];

	result.splice(
		indexToInsert === -1
			? result.length
			: indexToInsert,
		0,
		{
			id,
			name,
			kind: CommunityKind.Custom
		}
	);

	return result;
}

function removeCommunity(
	communities: readonly CommunityHubSidebarNodeModel[],
	id: number
): readonly CommunityHubSidebarNodeModel[] {
	return communities.filter((x) => x.id !== id);
}

function updateCategoryFollowStatus(
	categoryId: number,
	newFollowerStatus: boolean,
	item: HubItemHubSidebarNodeModel
): HubItemHubSidebarNodeModel {
	switch (item.kind) {
		case HubItemCategoryNodeKind.Category:
			return item.category
				.id === categoryId
				? {
					...item,
					category: {
						...item.category,
						isFollowing: newFollowerStatus
					}
				}
				: item;
		case HubItemCategoryNodeKind.Group:
			return {
				...item,
				children: item.children
					.map(
						x => updateCategoryFollowStatus(
							categoryId,
							newFollowerStatus,
							x
						)
					)
			};
		default:
			never(item);
	}
}
export type HubSideBarNewItemReductionTarget = {
	itemKind: Exclude<HubDataSource, HubDataSource.Community>;
	categoryIds: readonly number[];
} | {
	itemKind: HubDataSource.Community;
	communityId: number;
};

export function reduceHubSidebarNewItemCountByOne(
	rootItems: readonly RootHubSidebarNodeModel[],
	target: HubSideBarNewItemReductionTarget
): readonly RootHubSidebarNodeModel[] {
	return rootItems
		.map(
			(rootItem): RootHubSidebarNodeModel => {
				switch (rootItem.source) {
					case HubDataSource.Announcement:
					case HubDataSource.KnowledgeItem:
					case HubDataSource.Event:
						if (rootItem.source !== target.itemKind) {
							return rootItem;
						}

						return {
							...rootItem,
							newItemsCount: Math.max(0, rootItem.newItemsCount - 1),
							children: rootItem
								.children
								.map(
									x => subtractOneNewItemCount(
										target.categoryIds,
										x
									)
								)
						};
					case HubDataSource.Community:
						return target.itemKind !== HubDataSource.Community
							? rootItem
							: {
								...rootItem,
								newCommunityIds: rootItem.newCommunityIds
									.filter(
										x => x !== target.communityId
									)
							};
					default:
						return never(rootItem);
				}
			}
		);
}

function subtractOneNewItemCount(
	categoryIds: readonly number[],
	item: HubItemHubSidebarNodeModel
): HubItemHubSidebarNodeModel {
	switch (item.kind) {
		case HubItemCategoryNodeKind.Category:
			const { category, newItemsCount } = item;

			if (!categoryIds.includes(category.id)) {
				return item;
			}

			return {
				...item,
				newItemsCount: Math.max(0, newItemsCount - 1)
			};
		case HubItemCategoryNodeKind.Group:
			return {
				...item,
				children: item
					.children
					.map(
						x => subtractOneNewItemCount(
							categoryIds,
							x
						)
					)
			};
		default:
			never(item);
	}
}

export type CommunityCreationPermissions = {
	canCreateCommunity: boolean;
	canCreateCommunityPost: boolean;
};

export const HubLayoutForRouter: FunctionComponent<PropsWithChildren<{}>> = connectToRouter(
	HubLayout,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);
